<template>
    <Dashboard
        :title="__('Transaction details', null, $page.props.profile_locale)"
    >
        <Heading
            :label="__('Transaction details', null, $page.props.profile_locale)"
        />
        <div>
            <div class="overflow-auto w-full shadow-md rounded-md">
                <table class="w-full whitespace-nowrap">
                    <thead>
                        <tr
                            class="text-xs font-semibold tracking-wide text-left text-gray-200 uppercase border-b bg-gray-900 border-red-700">
                            <th class="px-4 py-3">{{__('Order ID', null, $page.props.profile_locale)}}</th>
                            <th class="px-4 py-3">{{__('Email', null, $page.props.profile_locale)}}</th>
                            <th class="px-4 py-3">{{__('Payments', null, $page.props.profile_locale)}} / {{__('Currency', null, $page.props.profile_locale)}}</th>
                            <th class="px-4 py-3">{{__('Locale', null, $page.props.profile_locale)}}</th>
                            <th class="px-4 py-3" v-if="transaction.coupon">{{__('Coupon', null, $page.props.profile_locale)}}</th>
                            <th class="px-4 py-3">{{__('Shipping', null, $page.props.profile_locale)}}</th>
                            <th class="px-4 py-3">{{__('Total', null, $page.props.profile_locale)}}</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
                        <tr class="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-900 text-gray-700 dark:text-gray-400">
                            <td class="px-4 py-3">#{{transaction.order_nr}}</td>
                            <td class="px-4 py-3">{{transaction.email}}</td>
                            <td class="px-4 py-3">{{ transaction.payments + ' / ' + transaction.currency }}</td>
                            <td class="px-4 py-3">{{ transaction.locale }}</td>
                            <td class="px-4 py-3 text-sm font-bold" v-if="transaction.coupon">
                                <span v-if="transaction.coupon">{{transaction.coupon.name + ' (' + getDiscount(transaction.coupon) + ')'}}</span>
                                <span v-else> -- </span>
                            </td>
                            <td class="px-4 py-3 text-sm font-bold">
                                {{ getPrice(transaction.shipping_price) }}
                            </td>
                            <td class="px-4 py-3">
                                <span
                                    class="px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100 whitespace-nowrap"
                                >
                                    {{ getPrice(transaction.total) }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <h1 class="text-base font-bold mt-8 border-b-2 mb-1 border-red-700">{{__('Billing Address', null, $page.props.profile_locale)}}</h1>
            <ul class="shadow-md overflow-hidden font-semibold text-sm">
                <li class="even:bg-gray-100 p-2">{{ transaction.user_data.name + ' / ' + transaction.user_data.contact_name}}</li>
                <li class="even:bg-gray-100 p-2">{{ transaction.user_data.email }}</li>
                <li class="even:bg-gray-100 p-2">{{ transaction.user_data.phone }}</li>
                <li class="even:bg-gray-100 p-2">{{ transaction.user_data.address + ', ' + transaction.user_data.zip + ' ' + transaction.user_data.city }}</li>
                <li class="even:bg-gray-100 p-2" v-if="transaction.user_data.additional_address">{{ transaction.user_data.additional_address }}</li>
                <li class="even:bg-gray-100 p-2">
                    <div class="flex items-center">
                        {{ __('countries.' + transaction.user_data.country) }}
                        <img class="mx-1 w-6 h-6 max-w-max" :src="getLangImage(transaction.user_data.country)">
                    </div>
                </li>
            </ul>

            <h1 class="text-base font-bold mt-8">{{__('Product details', null, $page.props.profile_locale)}}</h1>
            <ul class="space-y-2 shadow-md">
                <li
                    v-for="item in transaction.products" :key="item.id"
                    class="bg-gray-50 hover:bg-gray-100 text-gray-700 p-2"
                >
                <div class="flex sm:flex-row flex-col justify-between">
                    <div class="flex lg:flex-nowrap flex-wrap items-center justify-start text-sm">
                        <CanvasPreview
                            v-if="item.product_details.product.options.text_engrave == true"
                            :item="item"
                            :id="`thumbnails-canvas-${item.id}`"
                            class="ml-0"
                            style="zoom: 0.5;"
                        />
                        <img
                            v-else
                            :src="item.product_details.product.options.text_engrave == true ? item.product_details.product_variation.engrave.image.url : item.product_details.product.sortedImages[0].link"
                            class="shadow-md mr-2"
                            width="150"
                        />
                        <div class="space-y-2 mt-2 ml-2">
                            <p class="font-bold" v-html="item.product_details.product.name"></p>
                            <p class="text-xs text-gray-600" v-html="item.product_details.product.short_description"></p>

                            <p class="text-sm mt-5" v-if="item.product_details.product_variant">{{__('Selected variant', null, $page.props.profile_locale)}}</p>
                            <p class="font-bold" v-if="item.product_details.product_variant" v-html="item.product_details.product_variant.name"></p>

                            <div class="space-x-1 space-y-1">
                                <Button
                                    v-if="item.product_details.product.options.text_engrave == true"
                                    @click="previewModal = item"
                                    padding="sm"
                                >{{__('Preview', null, $page.props.profile_locale)}}</Button>
                                <Button
                                    v-if="$page.props.auth.user.type == 'admin' && canCopy"
                                    @click="copyCanvas = true"
                                    padding="sm"
                                    :class="[
                                        copyCanvas ? 'opacity-50' : '',
                                    ]"
                                >
                                    {{__('Copy', null, $page.props.profile_locale)}}
                                    <Spinner
                                        v-if="copyCanvas"
                                        class="h-5 w-5 text-gray-200 ml-1"
                                    />
                                </Button>
                            </div>

                            <CanvasPreview
                                v-if="copyCanvas"
                                id="copy-canvas"
                                class="hidden w-0 h-0 absolute"
                                @copy="copyCanvas = false"
                                type="copy"
                                :item="item"
                            />
                        </div>
                    </div>
                    <div class="px-4 py-2 text-base">
                        <div class="m-0.5 mb-3">
                            <div class="bg-gray-900 px-1 rounded-md text-gray-200 text-center">{{__('Quantity', null, $page.props.profile_locale)}}</div>
                            <div
                                class="font-bold text-center text-green-700 bg-green-100 border border-green-100 px-2 rounded-md mt-1"
                                v-html="getPieces(item)"
                            >
                            </div>
                        </div>
                        <div class="m-0.5">
                            <div class="bg-gray-900 px-1 rounded-md text-gray-200 text-center">{{__('Total', null, $page.props.profile_locale)}}</div>
                            <div
                                class="font-bold text-green-700 bg-green-100 border border-green-100 px-2 rounded-md mt-1 whitespace-nowrap"
                            >
                                {{ getPrice(item.price) }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="grid md:grid-cols-2 grid-cols-1 gap-2 p-3 mt-3 border-t-2 border-gray-300">
                    <ul class="space-y-2" v-if="item.product_details.font || item.product_details.pictogram || item.comment || item.color">
                        <li v-if="item.product_details.font">
                            <span class="font-bold">{{ __('Font', null, $page.props.profile_locale) + ': ' }}</span>
                            <span :style="'font-family:' + item.product_details.font.family">{{item.product_details.font.name}}</span>
                        </li>
                        <li class="flex items-center" v-if="item.product_details.pictogram">
                            <span class="font-bold">{{ __('Images', null, $page.props.profile_locale) + ': ' }}</span>
                            <img class="h-10 ml-2" :src="item.product_details.pictogram.url" />
                        </li>
                        <li class="flex flex-col justify-center" v-if="item.color">
                            <span class="font-bold">{{ __('Color', null, $page.props.profile_locale) + ': ' }}</span>
                            <div class="flex items-center">
                                <div class="ml-1 h-10 w-10 rounded-full border-2" :style="'background-color:' + item.color.color"></div>
                                <span>{{ item.color.title }}</span>
                            </div>
                        </li>
                        <li class="flex flex-col" v-if="item.comment">
                            <span class="font-bold">{{ __('Aditional info', null, $page.props.profile_locale) + ': ' }}</span>
                            <div v-html="item.comment.replace(/\n/g, '<br />')"></div>
                        </li>
                    </ul>
                    <div v-if="item.content">
                        <span class="font-bold">{{ __('Contents', null, $page.props.profile_locale) + ': ' }}</span>
                        <div class="whitespace-pre">{{ item.content }}</div>
                    </div>
                </div>
                </li>
            </ul>

            <CustomModal
                v-if="previewModal"
                @update="ev => { previewModal = null }"
                :title="__('Preview product', null, $page.props.profile_locale)"
            >
                <CanvasPreview
                    :item="previewModal"
                />
            </CustomModal>
        </div>
    </Dashboard>
</template>

<script>
    import Dashboard from '@/Layouts/Dashboard';
    import Button from 'UI/Button';
    import SvgIcon from 'UI/SvgIcon';
    import IconLink from 'UI/IconLink';
    import CustomModal from 'UI/CustomModal';
    import CanvasPreview from 'COMP/Canvas/CanvasPreview';

    import Heading from 'COMP/Heading';

    import {
        getPrice,
        getPieces,
        getPackagePrice,
        getDiscount,
        getLangImage
    } from 'helper';

    import { ref } from '@vue/reactivity';
    import Spinner from 'UI/Spinner';

    export default {
        props: {
            transaction: Object
        },
        components: {
            Dashboard, Button, SvgIcon, IconLink, Heading, CustomModal, CanvasPreview, Spinner
        },
        setup(props) {
            var newStyle = document.createElement('style');
            const arrFont = ref([]);
            props.transaction.products.forEach((el, key) => {
                if (el.product_details.product.options.fonts == true && el.product_details.font.src && !arrFont.value.includes(el.product_details.font.name)) {
                    newStyle.appendChild(document.createTextNode("\
                        @font-face {\
                            font-family: '"+el.product_details.font.family+"';\
                            src: url('/storage/fonts/"+el.product_details.font.src+"');\
                        }\
                    "));
                    arrFont.value.push(el.product_details.font.name);
                }
            })

            document.head.appendChild(newStyle);
            const previewModal = ref(null);
            const copyCanvas = ref(false);
            const canCopy = ref(true);

            askWritePermission();

            async function askWritePermission()
            {
                try
                {
                    const
                    {
                        state
                    } = await navigator.permissions.query(
                    {
                        name: 'clipboard-write',
                        allowWithoutGesture: false
                    })
                    return state === 'granted'
                }
                catch (error)
                {
                    canCopy.value = false;
                    console.log(`Compatibility error (ONLY CHROME > V66): ${error.message}`)
                    return false;
                }
            }

            return {
                getPrice,
                getPackagePrice,
                getPieces,
                getDiscount,

                previewModal,
                copyCanvas,
                canCopy,
                getLangImage
            }
        },

    }
</script>
